import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, X, Loader2, PenTool, Info, Download } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { usePublicQuote } from '@/hooks/usePublicQuote';
import { QuoteTemplateClassic } from '@/components/templates/quote/QuoteTemplateClassic';
import { QuoteTemplateModern } from '@/components/templates/quote/QuoteTemplateModern';
import { QuoteTemplateCorporate } from '@/components/templates/quote/QuoteTemplateCorporate';
import { QuoteTemplateElegant } from '@/components/templates/quote/QuoteTemplateElegant';
import type { QuoteData, QuoteItem, TemplateSettings } from '@/components/templates/types';

/**
 * Mappe les données API publique vers QuoteData (format attendu par les templates devis)
 */
function mapToQuoteData(quote: any): QuoteData {
  const rawItems = quote.items || [];

  const items: QuoteItem[] = rawItems.map((item: any) => ({
    description: item.description || '',
    unitPrice: Number(item.unitPrice) || 0,
    quantity: Number(item.quantity) || 0,
    total: Number(item.totalHT) || 0,
    totalHT: Number(item.totalHT) || 0,
    vatRate: Number(item.taxPercent) || 0,
    taxPercent: Number(item.taxPercent) || 0,
    vatAmount: (Number(item.totalHT) || 0) * ((Number(item.taxPercent) || 0) / 100),
  }));

  const quoteData: any = {
    id: quote.id,
    quoteNumber: quote.number || '',
    issueDate: quote.issueDate || '',
    date: quote.issueDate || '',
    validUntil: quote.validUntil || '',
    title: quote.title || '',
    clientName: quote.Client?.fullName || '',
    clientCompany: quote.Client?.companyName || '',
    clientAddressLine1: quote.Client?.addressLine1 || quote.Client?.address || '',
    clientCity: quote.Client?.city || '',
    clientZipCode: quote.Client?.zipCode || quote.Client?.postalCode || '',
    clientCountryCode: quote.Client?.countryCode || quote.Client?.country || '',
    clientPhone: quote.Client?.phone || '',
    clientEmail: quote.Client?.email || '',
    // Objet Client imbriqué (utilisé par ClientInfoSection)
    Client: quote.Client || null,
    items,
    subtotal: parseFloat(String(quote.totalHT)) || 0,
    totalHT: parseFloat(String(quote.totalHT)) || 0,
    tax: parseFloat(String(quote.totalTVA)) || 0,
    totalTVA: parseFloat(String(quote.totalTVA)) || 0,
    total: parseFloat(String(quote.totalTTC)) || 0,
    totalTTC: parseFloat(String(quote.totalTTC)) || 0,
    discountType: quote.discountType,
    discountValue: quote.discountValue ? parseFloat(quote.discountValue) : undefined,
    discountAmount: quote.discountAmount ? parseFloat(String(quote.discountAmount)) : undefined,
    notes: quote.notes || '',
    termsConditions: quote.termsConditions || '',
    status: quote.status,
  };

  return quoteData;
}

/**
 * Construit un objet Company compatible avec le template system
 */
function buildCompanyFromPublicData(quote: any): any {
  const c = quote.Company;
  if (!c) return null;

  return {
    id: c.id,
    tradeName: c.tradeName || c.legalName || '',
    legalName: c.legalName || '',
    logoUrl: c.logoUrl,
    addressLine1: c.addressLine1 || c.address || '',
    city: c.city || '',
    zipCode: c.zipCode || c.postalCode || '',
    countryCode: c.countryCode || c.country || 'FR',
    phone: c.phone || '',
    email: c.email || '',
    registrationNumber: c.registrationNumber || '',
    vatNumber: c.vatNumber || '',
    vatRegime: c.vatRegime,
    vatExemptArticle: c.vatExemptArticle,
    legalForm: c.legalForm,
    capital: c.capital,
    rcsCity: c.rcsCity,
    rcsNumber: c.rcsNumber,
    apeCode: c.apeCode,
    iban: c.iban,
    bic: c.bic,
    legalNotesDefault: c.legalNotesDefault || '',
    defaultPaymentTermsDays: c.defaultPaymentTermsDays,
  };
}

/**
 * Construit les TemplateSettings à partir de la réponse API publique
 */
function buildTemplateSettings(quote: any): TemplateSettings | undefined {
  const tpl = quote.template;
  if (!tpl) return undefined;

  return {
    id: tpl.id,
    name: tpl.name,
    displayName: tpl.displayName,
    styleConfig: typeof tpl.styleConfig === 'string'
      ? JSON.parse(tpl.styleConfig)
      : tpl.styleConfig,
    layoutConfig: typeof tpl.layoutConfig === 'string'
      ? JSON.parse(tpl.layoutConfig)
      : tpl.layoutConfig,
  };
}

/**
 * Détermine la couleur primaire.
 * Priorité: 1) colorPrimary de templatePreference  2) styleConfig du template  3) fallback
 */
function resolveColor(quote: any): string {
  const pref = quote.templatePreference;
  if (pref?.colorPrimary) {
    return pref.colorPrimary;
  }
  const tpl = quote.template;
  if (tpl?.styleConfig) {
    const styleConfig = typeof tpl.styleConfig === 'string'
      ? JSON.parse(tpl.styleConfig)
      : tpl.styleConfig;
    if (styleConfig?.primaryColor) return styleConfig.primaryColor;
  }
  return quote.Company?.quoteColor || '#2563eb';
}

/**
 * Résout le nom du template pour le routing
 */
function resolveTemplateName(quote: any): string {
  return quote.template?.displayName?.toLowerCase() || 'classic';
}

export function PublicQuoteView() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();

  // Utilisation du hook custom (Couche 2 : Hooks)
  const { quote, loading, error, accepting, accepted, acceptQuote } = usePublicQuote(token);

  // États locaux UI uniquement (pas de données)
  const [hasSignature, setHasSignature] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);

  const handleAccept = async () => {
    if (!quote || accepting || !hasSignature) return;

    const signatureData = signatureRef.current?.toDataURL('image/png');

    await acceptQuote({
      signature: signatureData,
      signedByName: quote.Client?.fullName || quote.Client?.companyName || 'N/A',
      signedByEmail: quote.Client?.email,
    });
  };

  const handleClearSignature = () => {
    signatureRef.current?.clear();
    setHasSignature(false);
  };

  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setHasSignature(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('quotes.public.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isDeactivated = error === 'LINK_DEACTIVATED';
    const isNotFound = error === 'LINK_NOT_FOUND';

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className={`w-16 h-16 ${isDeactivated ? 'bg-amber-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {isDeactivated ? (
              <Info className="w-8 h-8 text-amber-600" />
            ) : (
              <X className="w-8 h-8 text-red-600" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isDeactivated
              ? t('quotes.public.errorDeactivatedTitle')
              : isNotFound
                ? t('quotes.public.errorNotFoundTitle')
                : t('quotes.public.error')}
          </h2>
          <p className="text-gray-600 mb-6">
            {isDeactivated
              ? t('quotes.public.errorDeactivatedMessage')
              : isNotFound
                ? t('quotes.public.errorNotFoundMessage')
                : t('quotes.public.errorGenericMessage')}
          </p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return null;
  }

  // Couleur résolue depuis les préférences template ou fallback
  const quoteColor = resolveColor(quote);

  // Page de confirmation après l'acceptation
  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-2xl w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-12 h-12 text-green-600" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('quotes.public.thankYou')}
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            {t('quotes.public.acceptanceConfirmation', { number: quote.number })}
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('quotes.public.summary')}</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('quotes.number')}</span>
                <span className="font-medium text-gray-900">{quote.number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('quotes.public.acceptanceDate')}</span>
                <span className="font-medium text-gray-900">{new Date().toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('templates.common.totalTTC')}</span>
                <span className="font-bold text-lg" style={{ color: quoteColor }}>
                  {parseFloat(String(quote.totalTTC || 0)).toFixed(2)} €
                </span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left mb-8">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5" />
              {t('quotes.public.nextSteps')}
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>{t('quotes.public.emailConfirmation', { email: quote.Client?.email })}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>{t('quotes.public.teamContact')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>{t('quotes.public.invoiceSent')}</span>
              </li>
            </ul>
          </div>

          {(quote.Company?.email || quote.Company?.phone) && (
            <div className="text-sm text-gray-600">
              <p className="mb-2">{t('quotes.public.contactUs')}</p>
              <div className="flex flex-col gap-1">
                {quote.Company?.email && (
                  <a href={`mailto:${quote.Company.email}`} className="text-blue-600 hover:underline">
                    {quote.Company.email}
                  </a>
                )}
                {quote.Company?.phone && (
                  <a href={`tel:${quote.Company.phone}`} className="text-blue-600 hover:underline">
                    {quote.Company.phone}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mapper vers QuoteData pour les vrais templates devis
  const quoteData = mapToQuoteData(quote);
  const companyData = buildCompanyFromPublicData(quote);
  const templateSettings = buildTemplateSettings(quote);
  const templateName = resolveTemplateName(quote);

  // Props communes pour tous les templates
  const templateProps = {
    quoteData,
    onClose: () => { /* Page publique : pas de close */ },
    template: templateSettings,
    company: companyData,
    color: quoteColor,
  };

  // Sélection du bon template selon le displayName (même logique que QuoteTemplateRouter)
  const renderTemplate = () => {
    switch (templateName) {
      case 'modern':
        return <QuoteTemplateModern {...templateProps} />;
      case 'corporate':
        return <QuoteTemplateCorporate {...templateProps} />;
      case 'elegant':
        return <QuoteTemplateElegant {...templateProps} />;
      case 'classic':
      default:
        return <QuoteTemplateClassic {...templateProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Masquer l'ActionBar du template (fixed bottom-6 right-6 z-50) */}
      <style>{`.public-quote-wrapper .fixed.bottom-6.right-6.z-50 { display: none !important; }`}</style>

      {/* Header public : numéro de devis + téléchargement */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-200 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {quote.Company?.logoUrl && (
              <img
                src={quote.Company.logoUrl}
                alt={quote.Company.tradeName || quote.Company.legalName || ''}
                className="h-8 object-contain"
              />
            )}
            <span className="text-sm font-semibold text-gray-700">
              {quoteData.quoteNumber}
            </span>
          </div>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: quoteColor }}
          >
            <Download className="w-4 h-4" />
            {t('invoices.public.downloadPdf')}
          </button>
        </div>
      </div>

      {/* Rendu du document via le vrai template devis (Classic/Modern/Corporate/Elegant) */}
      <div className="public-quote-wrapper">
        {renderTemplate()}
      </div>

      {/* Section signature et acceptation - spécifique aux pages publiques */}
      {!accepted && quote.status !== 'accepted' && (
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <div className="bg-white shadow-lg p-8 md:p-12">
            {/* Canvas de signature */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-gray-700" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('quotes.public.electronicSignature')}</h3>
                  <span className="text-red-600 font-bold">*</span>
                </div>
                {hasSignature && (
                  <button
                    onClick={handleClearSignature}
                    className="text-sm text-gray-600 hover:text-gray-900 underline"
                  >
                    {t('quotes.public.clear')}
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t('quotes.public.signInBox')}
              </p>
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-40 touch-action-none',
                    style: { touchAction: 'none' }
                  }}
                  onEnd={handleSignatureEnd}
                />
              </div>
              {!hasSignature && (
                <p className="text-sm text-orange-600 mt-2 flex items-center gap-1">
                  <X className="w-4 h-4" />
                  {t('quotes.public.signRequired')}
                </p>
              )}
            </div>

            {/* Bouton d'acceptation */}
            <button
              onClick={handleAccept}
              disabled={accepting || !hasSignature}
              className="w-full disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2 text-lg"
              style={{
                backgroundColor: (accepting || !hasSignature) ? undefined : quoteColor,
                filter: (accepting || !hasSignature) ? undefined : 'brightness(1)',
              }}
              onMouseEnter={(e) => {
                if (!accepting && hasSignature) {
                  e.currentTarget.style.filter = 'brightness(0.9)';
                }
              }}
              onMouseLeave={(e) => {
                if (!accepting && hasSignature) {
                  e.currentTarget.style.filter = 'brightness(1)';
                }
              }}
            >
              {accepting ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  {t('quotes.public.accepting')}
                </>
              ) : (
                <>
                  <Check className="w-6 h-6" />
                  {t('quotes.public.acceptQuote')}
                </>
              )}
            </button>
            <p className="text-center text-sm text-gray-500 mt-4">
              {t('quotes.public.acceptanceDisclaimer')}
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center py-8 text-sm text-gray-500">
        <p>{t('quotes.public.autoGenerated')}</p>
      </div>
    </div>
  );
}
